import React, { useEffect, useState } from "react";
import { FiX, FiPlus } from "react-icons/fi";

interface Remark {
  id: number;
  remark: string;
  type: string;
  createdAt: string;
}

interface StudentRemarksModalProps {
  studentId: number;
  onClose: () => void;
  refreshList: () => void;
  role: "admin" | "mentor";
}

const StudentRemarksModal: React.FC<StudentRemarksModalProps> = ({
  studentId,
  onClose,
  refreshList,
  role,
}) => {
  const [remarks, setRemarks] = useState<Remark[]>([]);
  const [newRemark, setNewRemark] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const basePath =
    role === "admin" ? "/api/admin/students" : "/api/mentor/students";

  const fetchRemarks = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${basePath}/${studentId}/remarks`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(`Failed to load remarks (${res.status})`);
      setRemarks(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newRemark.trim()) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${basePath}/${studentId}/remarks`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ remark: newRemark }),
        }
      );
      if (!res.ok) throw new Error(`Add failed (${res.status})`);
      setNewRemark("");
      fetchRemarks();
      refreshList();
    } catch (e: any) {
      setError(e.message);
    }
  };

  useEffect(() => {
    fetchRemarks();
    // eslint-disable-next-line
  }, [studentId, role]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
        >
          <FiX size={20} />
        </button>
        <h2 className="text-xl font-bold text-[#009fe3] mb-4">
          Remarks for Student #{studentId}
        </h2>
        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : error ? (
          <p className="text-red-600">{error}</p>
        ) : (
          <ul className="space-y-3 max-h-64 overflow-y-auto mb-4">
            {remarks.map((r) => (
              <li
                key={r.id}
                className={`p-3 rounded border flex justify-between items-start ${
                  r.type === "MENTOR_NOTE"
                    ? "bg-yellow-50 border-yellow-200"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <div>
                  <span
                    className={`font-medium ${
                      r.type === "MENTOR_NOTE"
                        ? "text-yellow-700"
                        : "text-blue-700"
                    }`}
                  >
                    {r.type === "MENTOR_NOTE" ? "Mentor Note" : "Admin Note"}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {new Date(r.createdAt).toLocaleString()}
                  </span>
                  <p className="mt-1 text-gray-800">{r.remark}</p>
                </div>
              </li>
            ))}
            {remarks.length === 0 && (
              <li className="text-gray-500 text-sm">No remarks yet.</li>
            )}
          </ul>
        )}

        <div className="flex space-x-2">
          <input
            className="flex-grow px-4 py-2 border rounded focus:ring-2 focus:ring-[#009fe3]"
            value={newRemark}
            onChange={(e) => setNewRemark(e.target.value)}
            placeholder="Add a remark…"
          />
          <button
            onClick={handleAdd}
            className="flex items-center px-4 py-2 bg-[#009fe3] text-white rounded hover:bg-[#007cb5] transition"
          >
            <FiPlus className="mr-1" /> Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentRemarksModal;
